import {
  useFetcher,
  useNavigate,
  useRevalidator,
  useRouteLoaderData,
} from "@remix-run/react";
import { AlertCircle, Eye, EyeClosed } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { GlobalLoader } from "~/components/common/GlobalLoader";
import { Button } from "~/components/ui/Button";
import { DateOfBirthPicker } from "~/components/ui/DateOfBirthPicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/Input";
import { PhonePrefixSelector } from "~/components/ui/phone-prefix-selector";
import { cn, getMenuHref } from "~/lib/utils";
import { useAppStore } from "~/stores/app";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";
import { useAuth } from "~/stores/user";

export function AuthModal() {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const rootData = useRouteLoaderData<any>("root");
  const token = rootData?.token;
  const loginFetcher = useFetcher<any>();
  const signupFetcher = useFetcher<any>();
  const {
    authModalOpened,
    authModalType,
    authModalFrom,
    authModalBlocked,
    closeAuthModal,
    resetAuthModal,
  } = useAppStore();

  const isOpen = authModalOpened && !token;
  const modalType = authModalType || "login";
  const from = authModalFrom;
  const isBlocked = authModalBlocked;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [country, setCountry] = useState<string | undefined>();
  const [hasSubmittedSignup, setHasSubmittedSignup] = useState(false);
  const [hasSubmittedLogin, setHasSubmittedLogin] = useState(false);
  const [signupFormData, setSignupFormData] = useState<any>(null);
  const { bookingCart, setBookingCart, setHasSignupCustomerId } =
    useBookingCartStore();
  const [phonePrefix, setPhonePrefix] = useState<string>("+39");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
    trigger: triggerLogin,
  } = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });
  const {
    register: registerSignup,
    handleSubmit: handleSignupSubmit,
    setValue: setSignupValue,
    control: signupControl,
    formState: { errors: signupErrors },
    watch: watchSignupPassword,
    trigger: triggerSignup,
  } = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const onLoginSubmit = async (data: any) => {
    const isValid = await triggerLogin();
    if (!isValid) {
      return;
    }
    setHasSubmittedLogin(true);
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("password", data.password);
    loginFetcher.submit(formData, {
      method: "post",
      action: "/auth/login",
    });
  };

  const onSignupSubmit = async (data: any) => {
    setSignupValue("mobileNumber", phonePrefix + phoneNumber, {
      shouldValidate: true,
    });
    const isValid = await triggerSignup();
    if (!isValid) {
      return;
    }
    const fullPhoneNumber = phonePrefix + phoneNumber;
    if (!phoneNumber || phoneNumber.trim().length === 0) {
      return;
    }
    if (fullPhoneNumber.length < 8) {
      return;
    }
    setHasSubmittedSignup(true);
    if (from === "booking") {
      setSignupFormData({
        ...data,
        mobileNumber: fullPhoneNumber,
        zipCode: data.zipCode || "",
      });
    }
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("surname", data.surname);
    formData.append("zipCode", data.zipCode || "");
    formData.append("mobileNumber", fullPhoneNumber);
    formData.append("emailAddress", data.emailAddress);
    formData.append("password", data.password);
    formData.append("confirmPassword", data.confirmPassword);
    formData.append("privacyAgreed", data.privacyAgreed ? "on" : "");
    formData.append(
      "emailCommunicationAgreed",
      data.emailCommunicationAgreed ? "on" : "",
    );
    formData.append("profilingAgreed", data.profilingAgreed ? "on" : "");
    formData.append("dataSharingAgreed", data.dataSharingAgreed ? "on" : "");
    formData.append("dateOfBirth", data.dateOfBirth || "");
    formData.append("gender", data.gender || "");
    formData.append("modal", "true");
    formData.append("from", from || "");
    signupFetcher.submit(formData, {
      method: "post",
      action: "/auth/signup",
    });
  };

  const { openAuthModal } = useAppStore();

  const closeModal = () => {
    closeAuthModal();
  };

  const switchToSignup = () => {
    setHasSubmittedLogin(false);
    openAuthModal("signup", from || undefined);
  };

  const switchToLogin = () => {
    setHasSubmittedSignup(false);
    openAuthModal("login", from || undefined);
  };

  useEffect(() => {
    if (token && isOpen) {
      resetAuthModal();
      closeModal();
      if (from === "cart") {
        navigate("/shop/cart");
      } else if (from === "checkout") {
        navigate("/shop/checkout");
      } else if (from === "booking") {
      }
    }
  }, [token, isOpen, from, navigate, closeModal, resetAuthModal]);

  useEffect(() => {
    if (signupFetcher.data?.success && from === "booking") {
      fetch("/api/auth/init-session").catch((error) => {
        console.error("Failed to init session:", error);
      });
    }
  }, [signupFetcher.data, from]);

  useEffect(() => {
    if (!authModalOpened) {
      setHasSubmittedLogin(false);
      setHasSubmittedSignup(false);
    }
  }, [authModalOpened]);

  useEffect(() => {
    if (
      loginFetcher.data?.success &&
      loginFetcher.state === "idle" &&
      isOpen &&
      hasSubmittedLogin
    ) {
      setHasSubmittedLogin(false);
      resetAuthModal();
      revalidator.revalidate();
      closeModal();
      if (from === "cart") {
        navigate("/shop/cart");
      } else if (from === "checkout") {
        navigate("/shop/checkout");
      }
    }
  }, [
    loginFetcher.data,
    loginFetcher.state,
    from,
    navigate,
    closeModal,
    isOpen,
    revalidator,
    hasSubmittedLogin,
    resetAuthModal,
  ]);

  useEffect(() => {
    if (
      signupFetcher.data?.success &&
      signupFetcher.state === "idle" &&
      isOpen &&
      hasSubmittedSignup
    ) {
      setHasSubmittedSignup(false);

      if (from === "booking" && signupFormData) {
        setHasSignupCustomerId(true);

        const customReferentData = {
          name: signupFormData.name || "",
          surname: signupFormData.surname || "",
          email: signupFormData.emailAddress || "",
          phone: signupFormData.mobileNumber || "",
          zipCode: signupFormData.zipCode || "",
          dateOfBirth: signupFormData.dateOfBirth || "",
          privacyAgreed: !!signupFormData.privacyAgreed,
          emailCommunicationAgreed: !!signupFormData.emailCommunicationAgreed,
          profilingAgreed: !!signupFormData.profilingAgreed,
          dataSharingAgreed: !!signupFormData.dataSharingAgreed,
        };

        if (bookingCart) {
          setBookingCart({
            ...bookingCart,
            customReferentData,
          });
        } else {
          setBookingCart({
            BasketItems: [],
            customReferentData,
          });
        }
        setSignupFormData(null);
        resetAuthModal();
        closeModal();
      } else {
        if (signupFetcher.data.require_confirm_email) {
          const fromValue = from;
          const fromParam = fromValue ? `?from=${fromValue}` : "";
          resetAuthModal();
          closeModal();
          navigate(`/auth/signup/thank-you${fromParam}`);
        } else if (signupFetcher.data.redirect) {
          resetAuthModal();
          closeModal();
          navigate(signupFetcher.data.redirect);
        } else {
          switchToLogin();
        }
      }
    }
  }, [
    signupFetcher.data,
    signupFetcher.state,
    from,
    navigate,
    closeModal,
    switchToLogin,
    isOpen,
    hasSubmittedSignup,
    resetAuthModal,
  ]);

  useEffect(() => {
    if (country) {
      setSignupValue("country", country, { shouldValidate: true });
    }
  }, [country, setSignupValue]);

  useEffect(() => {
    if (!authModalOpened) {
      setCountry(undefined);
      setPhonePrefix("+39");
      setPhoneNumber("");
      const timer = setTimeout(() => {
        resetAuthModal();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [authModalOpened, resetAuthModal]);

  // Loader a tutto schermo durante le transizioni di autenticazione (chiamata
  // API + eventuale redirect): blocca l'utente con un feedback chiaro invece di
  // lasciare la modale aperta apparentemente ferma.
  // Keep it up while submitting and, after the action, only when it succeeded
  // (covers the close/redirect window). After a failed action the fetcher goes
  // through "loading" while Remix revalidates every loader: without the
  // success check the loader would sit on top of the field errors until the
  // revalidation ends (or forever, if an upstream call hangs).
  const isAuthLoading =
    loginFetcher.state === "submitting" ||
    signupFetcher.state === "submitting" ||
    (loginFetcher.state === "loading" && loginFetcher.data?.success) ||
    (signupFetcher.state === "loading" && signupFetcher.data?.success);
  const authLoadingMessage =
    signupFetcher.state !== "idle"
      ? t("Sto creando l'account")
      : t("Accesso in corso");

  return (
    <>
      {isAuthLoading && (
        <GlobalLoader message={authLoadingMessage} className="z-[100]" />
      )}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => !open && !isBlocked && closeModal()}
      >
      <DialogContent
        className={cn("max-w-md max-h-[90vh] overflow-y-auto", {
          "max-w-md": modalType === "login",
          "max-w-xl": modalType === "signup",
        })}
        hideCloseButton={isBlocked}
      >
        <DialogHeader>
          <DialogTitle>
            {modalType === "login" ? t("Entra nel sito") : t("Registrati")}
          </DialogTitle>
        </DialogHeader>

        {modalType === "login" ? (
          <div className="space-y-4">
            <form
              onSubmit={handleLoginSubmit(onLoginSubmit)}
              className="flex flex-col space-y-4"
            >
              {loginFetcher.data?.errorCode && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-destructive text-sm text-center">
                    {loginFetcher.data.errorCode === "INVALID_CREDENTIALS"
                      ? t(
                          "Credenziali non valide. Controlla email e password e riprova.",
                        )
                      : loginFetcher.data.error || t("Errore durante il login")}
                  </p>
                </div>
              )}
              {loginFetcher.data?.error && !loginFetcher.data?.errorCode && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-destructive text-sm text-center">
                    {loginFetcher.data.error}
                  </p>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1"
                >
                  {t("Inserisci Email")}
                </label>
                <Input
                  {...registerLogin("email", {
                    required: t("Il campo Email è obbligatorio"),
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: t("Indirizzo Email non valido"),
                    },
                  })}
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  autoComplete="email"
                />
                {(loginErrors.email ||
                  loginFetcher.data?.fieldErrors?.email) && (
                  <div className="text-destructive text-sm mt-1">
                    {loginErrors.email?.message ||
                      loginFetcher.data.fieldErrors.email}
                  </div>
                )}
              </div>

              <div className="relative">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-1"
                >
                  {t("Inserisci Password")}
                </label>
                <div className="relative">
                  <Input
                    {...registerLogin("password", {
                      required: t("Il campo Password è obbligatorio"),
                    })}
                    id="password"
                    name="password"
                    type={!showPassword ? "password" : "text"}
                    placeholder="Password"
                    className="pr-12"
                    autoComplete="current-password"
                  />
                  <div
                    className="absolute right-4 top-2 cursor-pointer"
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? (
                      <EyeClosed size={19} className="text-gray-500" />
                    ) : (
                      <Eye size={19} className="text-gray-500" />
                    )}
                  </div>
                </div>
                {(loginErrors.password ||
                  loginFetcher.data?.fieldErrors?.password) && (
                  <div className="text-destructive text-sm mt-1">
                    {loginErrors.password?.message ||
                      loginFetcher.data.fieldErrors.password}
                  </div>
                )}
              </div>

              <Button
                variant="secondary"
                className="w-full"
                type="submit"
                disabled={loginFetcher.state === "submitting"}
              >
                {loginFetcher.state === "submitting"
                  ? t("Accesso in corso") + "..."
                  : t("Entra nel sito")}
              </Button>
            </form>

            <div className="text-center">
              <a
                href="/auth/reset-password"
                className="text-sm text-primary border-b-2 border-primary"
              >
                {t("Ho dimenticato la password")}
              </a>
            </div>

            <div className="text-center">
              <span className="text-sm">{t("oppure")}</span>
            </div>

            <Button className="w-full" onClick={switchToSignup}>
              {t("Registrati")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <form
              onSubmit={handleSignupSubmit(onSignupSubmit)}
              className="flex flex-col space-y-4"
            >
              {signupFetcher.data?.error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-destructive text-sm text-center">
                    {signupFetcher.data.error}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("Nome")}
                  </label>
                  <Input
                    {...registerSignup("name", {
                      required: t("Il campo Nome è obbligatorio"),
                    })}
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t("Nome")}
                    autoComplete="given-name"
                  />
                  {(signupErrors.name ||
                    signupFetcher.data?.fieldErrors?.name) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.name?.message ||
                        signupFetcher.data.fieldErrors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="surname"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("Cognome")}
                  </label>
                  <Input
                    {...registerSignup("surname", {
                      required: t("Il campo Cognome è obbligatorio"),
                    })}
                    id="surname"
                    name="surname"
                    type="text"
                    placeholder={t("Cognome")}
                    autoComplete="family-name"
                  />
                  {(signupErrors.surname ||
                    signupFetcher.data?.fieldErrors?.surname) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.surname?.message ||
                        signupFetcher.data.fieldErrors.surname}
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="country"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("Paese")}
                  </label>
                  <input
                    {...registerSignup("country", {
                      required: t("Il paese è obbligatorio"),
                    })}
                    id="country"
                    name="country"
                    type="hidden"
                    value={country || ""}
                  />
                  <CountryDropdown
                    searchLabel={t("Cerca") + "..."}
                    placeholder={t("Seleziona")}
                    defaultValue={country}
                    onChange={(selectedCountry) => {
                      setCountry(selectedCountry.alpha2);
                      setSignupValue("country", selectedCountry.alpha2, {
                        shouldValidate: true,
                      });
                    }}
                  />
                  {(signupErrors.country ||
                    signupFetcher.data?.fieldErrors?.country) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.country?.message ||
                        signupFetcher.data?.fieldErrors?.country}
                    </div>
                  )}
                </div>
              </div> */}
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                  <label
                    htmlFor="mobileNumber"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("Numero di telefono")}
                  </label>
                  <div className="flex gap-2">
                    <PhonePrefixSelector
                      value={phonePrefix}
                      onChange={(newPrefix) => {
                        setPhonePrefix(newPrefix);
                        const fullNumber = newPrefix + phoneNumber;
                        setSignupValue("mobileNumber", fullNumber, {
                          shouldValidate: true,
                        });
                      }}
                      defaultValue="+39"
                      searchLabel={t("Cerca paese o prefisso") + "..."}
                    />
                    <Input
                      {...registerSignup("mobileNumber", {
                        required: t("Il campo Telefono è obbligatorio"),
                        validate: () => {
                          const fullNumber = phonePrefix + phoneNumber;
                          if (!phoneNumber || phoneNumber.trim().length === 0) {
                            return t("Il campo Telefono è obbligatorio");
                          }
                          if (fullNumber.length < 8) {
                            return t("Il numero di telefono non è valido");
                          }
                          return true;
                        },
                        onChange: (e) => {
                          let value = e.target.value;
                          value = value.replace(/^\+/, "");
                          value = value.replace(/\D/g, "");
                          setPhoneNumber(value);
                          setSignupValue("mobileNumber", phonePrefix + value, {
                            shouldValidate: true,
                          });
                        },
                      })}
                      id="mobileNumber"
                      name="mobileNumber"
                      type="tel"
                      placeholder="330000000"
                      value={phoneNumber}
                      required
                      autoComplete="tel"
                      className="flex-1"
                    />
                  </div>
                  {(signupErrors.mobileNumber ||
                    signupFetcher.data?.fieldErrors?.mobileNumber) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.mobileNumber?.message ||
                        signupFetcher.data.fieldErrors.mobileNumber}
                    </div>
                  )}
                </div>

                <div className="col-span-1">
                  <label
                    htmlFor="zipCode"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("CAP")}
                  </label>
                  <Input
                    {...registerSignup("zipCode", {
                      required: t("Il campo CAP è obbligatorio"),
                    })}
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    required
                    placeholder={t("CAP")}
                    autoComplete="postal-code"
                  />
                  {(signupErrors.zipCode ||
                    signupFetcher.data?.fieldErrors?.zipCode) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.zipCode?.message ||
                        signupFetcher.data.fieldErrors.zipCode}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="dateOfBirth"
                  className="block text-sm font-medium mb-1"
                >
                  {t("Data di nascita")} *
                </label>
                <Controller
                  name="dateOfBirth"
                  control={signupControl}
                  rules={{
                    required: t("Il campo Data di nascita è obbligatorio"),
                  }}
                  render={({ field }) => (
                    <DateOfBirthPicker
                      id="dateOfBirth"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      hasError={
                        !!(
                          signupErrors.dateOfBirth ||
                          signupFetcher.data?.fieldErrors?.dateOfBirth
                        )
                      }
                      placeholder={t("Seleziona la data")}
                    />
                  )}
                />
                {(signupErrors.dateOfBirth ||
                  signupFetcher.data?.fieldErrors?.dateOfBirth) && (
                  <div className="text-destructive text-sm mt-1">
                    {(signupErrors.dateOfBirth?.message as string) ||
                      signupFetcher.data.fieldErrors.dateOfBirth}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("Sesso")} *
                </label>
                <Controller
                  name="gender"
                  control={signupControl}
                  rules={{ required: t("Il campo Sesso è obbligatorio") }}
                  render={({ field }) => (
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: "2", label: t("Uomo") },
                        { value: "1", label: t("Donna") },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => field.onChange(option.value)}
                          aria-pressed={field.value === option.value}
                          className={cn(
                            "flex h-9 items-center justify-center border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                            field.value === option.value
                              ? "border-secondary bg-secondary text-white"
                              : "text-muted-800 hover:border-secondary",
                            (signupErrors.gender ||
                              signupFetcher.data?.fieldErrors?.gender) &&
                              !field.value &&
                              "border-destructive",
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                />
                {(signupErrors.gender ||
                  signupFetcher.data?.fieldErrors?.gender) && (
                  <div className="text-destructive text-sm mt-1">
                    {(signupErrors.gender?.message as string) ||
                      signupFetcher.data.fieldErrors.gender}
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="emailAddress"
                  className="block text-sm font-medium mb-1"
                >
                  {t("Indirizzo Email")}
                </label>
                <Input
                  {...registerSignup("emailAddress", {
                    required: t("Il campo Email è obbligatorio"),
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: t("Indirizzo Email non valido"),
                    },
                  })}
                  id="emailAddress"
                  name="emailAddress"
                  type="email"
                  placeholder={t("Indirizzo Email")}
                  required
                  autoComplete="email"
                />
                {(signupErrors.emailAddress ||
                  signupFetcher.data?.fieldErrors?.emailAddress) && (
                  <div className="text-destructive text-sm mt-1">
                    {signupErrors.emailAddress?.message ||
                      signupFetcher.data.fieldErrors.emailAddress}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("Password")}
                  </label>
                  <div className="relative">
                    <Input
                      {...registerSignup("password", {
                        required: t("Il campo Password è obbligatorio"),
                        minLength: {
                          value: 6,
                          message: t(
                            "La password deve contenere almeno 6 caratteri",
                          ),
                        },
                      })}
                      id="password"
                      name="password"
                      type={!showPassword ? "password" : "text"}
                      placeholder={t("Password")}
                      className="pr-12"
                      required
                      autoComplete="new-password"
                    />
                    <div
                      className="absolute right-4 top-2 cursor-pointer"
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? (
                        <EyeClosed size={19} className="text-gray-500" />
                      ) : (
                        <Eye size={19} className="text-gray-500" />
                      )}
                    </div>
                  </div>
                  {(signupErrors.password ||
                    signupFetcher.data?.fieldErrors?.password) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.password?.message ||
                        signupFetcher.data.fieldErrors.password}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium mb-1"
                  >
                    {t("Conferma Password")}
                  </label>
                  <div className="relative">
                    <Input
                      {...registerSignup("confirmPassword", {
                        required: t(
                          "Il campo Conferma Password è obbligatorio",
                        ),
                        validate: (value) => {
                          const password = watchSignupPassword("password");
                          return (
                            value === password ||
                            t("Le password non corrispondono")
                          );
                        },
                      })}
                      id="confirmPassword"
                      name="confirmPassword"
                      type={!showConfirmPassword ? "password" : "text"}
                      placeholder={t("Conferma Password")}
                      className="pr-12"
                      required
                      autoComplete="new-password"
                    />
                    <div
                      className="absolute right-4 top-2 cursor-pointer"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                    >
                      {showConfirmPassword ? (
                        <EyeClosed size={19} className="text-gray-500" />
                      ) : (
                        <Eye size={19} className="text-gray-500" />
                      )}
                    </div>
                  </div>
                  {(signupErrors.confirmPassword ||
                    signupFetcher.data?.fieldErrors?.confirmPassword) && (
                    <div className="text-destructive text-sm mt-1">
                      {signupErrors.confirmPassword?.message ||
                        signupFetcher.data.fieldErrors.confirmPassword}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    {...registerSignup("privacyAgreed", {
                      required: t(
                        "Devi accettare le condizioni della Privacy Policy",
                      ),
                    })}
                    type="checkbox"
                    name="privacyAgreed"
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">
                    {t(
                      "Riconosco che i miei dati personali saranno trattati come descritto nella",
                    )}{" "}
                    <a
                      href={getMenuHref(i18n.language, "/privacy-policy")}
                      target="_blank"
                      className="text-secondary underline"
                    >
                      {t("Privacy Policy")}
                    </a>
                    {" *"}
                  </span>
                </label>
                {(signupErrors.privacyAgreed ||
                  signupFetcher.data?.fieldErrors?.privacyAgreed) && (
                  <div className="text-destructive text-sm mt-1">
                    {signupErrors.privacyAgreed?.message ||
                      signupFetcher.data.fieldErrors.privacyAgreed}
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      watchSignupPassword("emailCommunicationAgreed") &&
                      watchSignupPassword("profilingAgreed") &&
                      watchSignupPassword("dataSharingAgreed")
                    }
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSignupValue("emailCommunicationAgreed", checked);
                      setSignupValue("profilingAgreed", checked);
                      setSignupValue("dataSharingAgreed", checked);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary mt-0.5"
                  />
                  <span className="text-sm font-medium">
                    {t(
                      "Desidero ricevere vantaggi esclusivi, anteprime, promozioni riservate e offerte personalizzate.",
                    )}{" "}
                    <span className="text-xs text-gray-400 font-normal">{t("(facoltativo)")}</span>
                  </span>
                </label>
                <div className="ml-6 mt-2 space-y-2 pl-1">
                  <label className="flex items-start space-x-2">
                    <input
                      {...registerSignup("emailCommunicationAgreed")}
                      type="checkbox"
                      name="emailCommunicationAgreed"
                      className="rounded border-gray-300 text-primary focus:ring-primary mt-0.5"
                    />
                    <span className="text-sm">
                      {t(
                        "Desidero ricevere comunicazioni su promozioni, eventi e novità della spa e partner selezionati",
                      )}
                    </span>
                  </label>
                  <label className="flex items-start space-x-2">
                    <input
                      {...registerSignup("profilingAgreed")}
                      type="checkbox"
                      name="profilingAgreed"
                      className="rounded border-gray-300 text-primary focus:ring-primary mt-0.5"
                    />
                    <span className="text-sm">
                      {t(
                        "Desidero ricevere comunicazioni personalizzate in base ai miei interessi",
                      )}
                    </span>
                  </label>
                  <label className="flex items-start space-x-2">
                    <input
                      {...registerSignup("dataSharingAgreed")}
                      type="checkbox"
                      name="dataSharingAgreed"
                      className="rounded border-gray-300 text-primary focus:ring-primary mt-0.5"
                    />
                    <span className="text-sm">
                      {t(
                        "Desidero ricevere offerte e vantaggi speciali da partner selezionati",
                      )}
                    </span>
                  </label>
                </div>
              </div>

              <div className="w-full p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800">
                      {t("Riceverai un’email per verificare l’account.")}
                    </p>
                    <p className="text-sm text-amber-800">
                      {t(
                        "Senza verifica non potrai accedere al profilo e agli acquisti e prenotazioni.",
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                className="w-full"
                type="submit"
                disabled={signupFetcher.state === "submitting"}
              >
                {signupFetcher.state === "submitting"
                  ? t("Sto creando l'account") + "..."
                  : t("Crea Account")}
              </Button>
            </form>

            <div className="text-center">
              <p className="text-sm">
                {t("Hai già un account?")}{" "}
                <button
                  onClick={switchToLogin}
                  className="text-secondary underline"
                >
                  {t("Login")}
                </button>
              </p>
            </div>
          </div>
        )}
      </DialogContent>
      </Dialog>
    </>
  );
}
